import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { RiCloseFill } from "react-icons/ri";
import MeritoButton from "../common/MeritoButtons/MeritoButtons";
import CircularLoader from "../common/CircularLoader/CircularLoader";
import { uploadBankStatement } from "./api";
import { checkPdfPassword } from "../../utils/pdfPassword";
import {
  BankStatementUploadItem,
  PulseDocSummary,
  PulseRangeUploadInput,
  UploadQueueStatus,
} from "./types";
import {
  isDocumentPasswordRequired,
  isBankStatementPdf,
  NOT_SUBMITTED_STATUS,
  REQUIRED_PASSWORD_STATUS,
  PASSWORD_VERIFIED_DURATION_MS,
  sortByPriority,
  validateBankStatementFile,
} from "./utils";
import {
  PasswordInput,
  PasswordVerificationSuccess,
  PdfFileIcon,
  RetryAction,
  ViewDocumentButton,
} from "./DocumentList";
import { useBankStatementsActions } from "./BankStatementsContext";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import { UploadDropzone } from "./UploadDropzone";
import styles from "./BankStatements.module.scss";

type FirstTimeUploadVariant = "landing" | "review";

interface FileUploadQueueProps {
  scope: string;
  accountId?: number;
  accountNumber?: string;
  scheduleId?: number | null;
  autoSubmit?: boolean;
  compact?: boolean;
  stageImmediately?: boolean;
  hideTrigger?: boolean;
  dialogVariant?: boolean;
  firstTimeVariant?: FirstTimeUploadVariant;
  listHeader?: React.ReactNode;
  reviewListChildren?: React.ReactNode;
  accountListChildren?: React.ReactNode;
  onItemsChange?: (items: BankStatementUploadItem[]) => void;
  onReady?: (docIds: number[]) => Promise<void>;
}

export interface FileUploadQueueHandle {
  openFilePicker: (rangeUpload?: PulseRangeUploadInput) => void;
}

const IN_PROGRESS_UPLOAD_STATUSES = new Set<UploadQueueStatus>([
  "queued",
  "checking",
  "uploading",
]);

const BUSY_UPLOAD_STATUSES = new Set<UploadQueueStatus>([
  "checking",
  "uploading",
]);

const RETRYABLE_UPLOAD_STATUSES = new Set<UploadQueueStatus>([
  "failed",
  "password_required",
]);

const SETTLED_UPLOAD_STATUSES = new Set<UploadQueueStatus>([
  "uploaded",
  "password_verified",
]);

const makeLocalId = (file: File, index: number) =>
  `${Date.now()}-${index}-${file.name}-${file.size}`;

const UploadQueueItem = ({
  item,
  dialogVariant,
  analysisLoading,
  onViewError,
  onRemove,
  onPasswordChange,
  onPasswordSubmit,
}: {
  item: BankStatementUploadItem;
  dialogVariant: boolean;
  analysisLoading: boolean;
  onViewError: (message: string) => void;
  onRemove: () => void;
  onPasswordChange: (password: string) => void;
  onPasswordSubmit: () => void;
}) => {
  const passwordRequired = item.status === "password_required";
  const isRemovable =
    passwordRequired ||
    (!item.rangeProcessing &&
      item.status !== "password_verified" &&
      (RETRYABLE_UPLOAD_STATUSES.has(item.status) ||
        (SETTLED_UPLOAD_STATUSES.has(item.status) && !analysisLoading)));
  const documentMeta =
    item.error ||
    (item.rangeProcessing
      ? "processing"
      : item.status.replace(/_/g, " "));

  return (
    <div
      className={`${styles.queueItem} ${
        item.status === "password_required"
          ? dialogVariant
            ? styles.dialogPasswordQueueItem
            : styles.passwordDocumentItem
          : ""
      } ${
        item.status === "password_verified"
          ? styles.passwordVerifiedFileItem
          : ""
      }`}
    >
      <div className={styles.queueItemMain}>
        <PdfFileIcon />
        <div className={styles.documentDetails}>
          <div className={styles.documentName} title={item.file.name}>
            {item.file.name}
          </div>
          {!dialogVariant &&
          item.status !== "password_verified" &&
          item.status !== "password_required" ? (
            <div
              className={`${styles.documentMeta} ${
                item.error ? styles.errorText : ""
              }`}
            >
              {documentMeta}
            </div>
          ) : null}
        </div>
        <div className={styles.documentActions}>
          {dialogVariant && item.status === "uploaded" ? (
            <span
              className={styles.documentActionPlaceholder}
              aria-hidden="true"
            />
          ) : (
            <ViewDocumentButton file={item.file} onError={onViewError} />
          )}
          {isRemovable ? (
            <span title="Delete statement">
              <MeritoButton
                type="tertiary"
                size="md2"
                onlyIcon
                hover={false}
                icon={<RiCloseFill />}
                onClick={onRemove}
              />
            </span>
          ) : (
            <span className={styles.documentProcessing} title="Processing">
              <CircularLoader width="1.5rem" height="1.5rem" />
            </span>
          )}
        </div>
      </div>
      {item.status === "password_required" ? (
        <PasswordInput
          value={item.password || ""}
          error={item.error}
          loading={item.passwordVerifying}
          message={
            dialogVariant ? "This file is password-protected." : undefined
          }
          onChange={onPasswordChange}
          onSubmit={onPasswordSubmit}
        />
      ) : null}
      {item.status === "password_verified" ? (
        <PasswordVerificationSuccess />
      ) : null}
    </div>
  );
};

export const FileUploadQueue = forwardRef<
  FileUploadQueueHandle,
  FileUploadQueueProps
>(({
    scope,
    accountId,
    accountNumber,
    scheduleId: scheduleIdOverride,
    autoSubmit = false,
    compact = false,
    stageImmediately = true,
    hideTrigger = false,
    dialogVariant = false,
    firstTimeVariant,
    listHeader,
    reviewListChildren,
    accountListChildren,
    onItemsChange,
    onReady,
  }, ref) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pendingRangeUploadRef = useRef<PulseRangeUploadInput | undefined>();
  const autoAttemptKeyRef = useRef("");
  const operationChainRef = useRef<Promise<void>>(Promise.resolve());
  const passwordVerifiedTimersRef = useRef<Map<string, number>>(new Map());
  const passwordVerificationControllersRef = useRef<
    Map<string, AbortController>
  >(new Map());
  const [items, setItems] = useState<BankStatementUploadItem[]>([]);
  const [analysisError, setAnalysisError] = useState("");
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const {
    scheduleId: providerScheduleId,
    stageUploadedDocument,
    removeDocument,
    setScopeBusy,
    submitDocumentPassword,
  } = useBankStatementsActions();
  const scheduleId =
    scheduleIdOverride === undefined ? providerScheduleId : scheduleIdOverride;

  useImperativeHandle(ref, () => ({
    openFilePicker: (rangeUpload?: PulseRangeUploadInput) => {
      pendingRangeUploadRef.current = rangeUpload;
      inputRef.current?.click();
    },
  }));

  const updateItem = (
    localId: string,
    updates: Partial<BankStatementUploadItem>
  ) => {
    setItems((current) =>
      current.map((item) =>
        item.localId === localId ? { ...item, ...updates } : item
      )
    );
  };

  const clearPasswordVerifiedTimer = (localId: string) => {
    const timer = passwordVerifiedTimersRef.current.get(localId);
    if (timer != null) window.clearTimeout(timer);
    passwordVerifiedTimersRef.current.delete(localId);
  };

  useEffect(
    () => () => {
      passwordVerifiedTimersRef.current.forEach((timer) =>
        window.clearTimeout(timer)
      );
      passwordVerifiedTimersRef.current.clear();
      passwordVerificationControllersRef.current.forEach((controller) =>
        controller.abort()
      );
      passwordVerificationControllersRef.current.clear();
    },
    []
  );

  const queueOperation = <T,>(operation: () => Promise<T>): Promise<T> => {
    const queued = operationChainRef.current.then(operation, operation);
    operationChainRef.current = queued.then(
      () => undefined,
      () => undefined
    );
    return queued;
  };

  const uploadItem = async (
    item: BankStatementUploadItem,
    passwordRequired = false
  ) => {
    updateItem(item.localId, {
      status: "uploading",
      error: undefined,
      rangeProcessing: !!item.rangeUpload,
    });
    try {
      const response = await uploadBankStatement({
        file: item.file,
        accountNumber,
        scheduleId,
        passwordRequired,
        rangeUpload: item.rangeUpload,
      });
      const uploaded = response?.uploadedDocs?.[0];
      if (!response?.success || !uploaded) {
        throw new Error("The statement could not be uploaded.");
      }
      const requiresPassword =
        passwordRequired || isDocumentPasswordRequired(uploaded);
      // Stage a locally-shaped document rather than the raw server payload: a
      // server status other than NOT_SUBMITTED would flip isReadingStatements
      // and jump the module to the full-screen processing view before the user
      // has pressed Continue.
      const doc: PulseDocSummary = {
        id: uploaded.id,
        s3FileKey: uploaded.s3FileKey,
        fileName: item.file.name,
        fileType: item.file.type || "application/pdf",
        status: requiresPassword
          ? REQUIRED_PASSWORD_STATUS
          : NOT_SUBMITTED_STATUS,
        ocrProcessingStatus: null,
        createdAt: new Date().toISOString(),
        errorMessage: null,
        // Synthesise the range association from what we just sent, so the
        // missing-period row can show its processing state without waiting for
        // the server to echo the field back on a later load.
        uploadedForRange:
          uploaded.uploadedForRange ??
          (item.rangeUpload && accountId != null
            ? { ...item.rangeUpload, accountId }
            : null),
      };
      if (stageImmediately) stageUploadedDocument(doc, accountId);
      updateItem(item.localId, {
        status: requiresPassword ? "password_required" : "uploaded",
        password: undefined,
        doc,
        // Stays raised until the analysis submit clears the queue; a range
        // upload is not "done" just because the file landed.
        rangeProcessing: !!item.rangeUpload,
      });
      return doc;
    } catch (error) {
      updateItem(item.localId, {
        status: "failed",
        password: undefined,
        rangeProcessing: false,
        error:
          getApiErrorMessage(error, "The statement could not be uploaded."),
      });
    }
  };

  const inspectAndUpload = async (item: BankStatementUploadItem) => {
    const validationError = validateBankStatementFile(item.file);
    if (validationError) {
      updateItem(item.localId, {
        status: "failed",
        error: validationError,
        rangeProcessing: false,
      });
      return;
    }
    updateItem(item.localId, {
      status: "checking",
      error: undefined,
      rangeProcessing: !!item.rangeUpload,
    });
    const protection = await checkPdfPassword(item.file);
    if (protection === "error") {
      updateItem(item.localId, {
        status: "failed",
        error: "This PDF could not be read. Try exporting it again.",
        rangeProcessing: false,
      });
      return;
    }
    // A locked PDF is uploaded straight away; the password is applied later
    // against the same document id.
    await uploadItem(item, protection === "required");
  };

  const addFiles = async (
    files: File[],
    rangeUpload?: PulseRangeUploadInput
  ) => {
    const pdfFiles = files.filter(isBankStatementPdf);
    if (!pdfFiles.length) return;
    const addedAt = Date.now();
    const nextItems = pdfFiles.map((file, index) => ({
      localId: makeLocalId(file, index),
      file,
      status: "queued" as const,
      addedAt,
      rangeUpload,
      rangeProcessing: !!rangeUpload,
    }));
    try {
      setAnalysisError("");
      autoAttemptKeyRef.current = "";
      setItems((current) => [...current, ...nextItems]);
      await queueOperation(async () => {
        for (const item of nextItems) await inspectAndUpload(item);
      });
    } catch (_error) {
      setItems((current) =>
        current.map((item) =>
          nextItems.some((candidate) => candidate.localId === item.localId)
            ? { ...item, rangeProcessing: false }
            : item
        )
      );
      setAnalysisError("The selected statements could not be processed.");
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const submitPassword = async (item: BankStatementUploadItem) => {
    if (
      !item.password ||
      !item.doc ||
      passwordVerificationControllersRef.current.has(item.localId)
    ) {
      return;
    }
    const password = item.password;
    const doc = item.doc;
    const controller = new AbortController();
    passwordVerificationControllersRef.current.set(item.localId, controller);
    updateItem(item.localId, {
      error: undefined,
      passwordVerifying: true,
      rangeProcessing: !!item.rangeUpload,
    });
    try {
      const updatedDoc = await queueOperation(() =>
        submitDocumentPassword(doc, password, {
          file: item.file,
          submitAfterSave: autoSubmit,
          signal: controller.signal,
        })
      );
      if (controller.signal.aborted) return;
      updateItem(item.localId, {
        status: "password_verified",
        password: undefined,
        passwordVerifying: false,
        doc: updatedDoc,
        rangeProcessing: !!item.rangeUpload,
      });
      clearPasswordVerifiedTimer(item.localId);
      const timer = window.setTimeout(() => {
        passwordVerifiedTimersRef.current.delete(item.localId);
        updateItem(item.localId, { status: "uploaded" });
      }, PASSWORD_VERIFIED_DURATION_MS);
      passwordVerifiedTimersRef.current.set(item.localId, timer);
    } catch (error) {
      if (controller.signal.aborted) return;
      updateItem(item.localId, {
        status: "password_required",
        passwordVerifying: false,
        error: getApiErrorMessage(error, "This PDF could not be verified."),
        rangeProcessing: !!item.rangeUpload,
      });
    } finally {
      if (
        passwordVerificationControllersRef.current.get(item.localId) ===
        controller
      ) {
        passwordVerificationControllersRef.current.delete(item.localId);
      }
    }
  };

  const removeItem = async (item: BankStatementUploadItem) => {
    const verificationController =
      passwordVerificationControllersRef.current.get(item.localId);
    verificationController?.abort();
    passwordVerificationControllersRef.current.delete(item.localId);
    if (BUSY_UPLOAD_STATUSES.has(item.status)) return;
    if (item.doc?.id) {
      try {
        await removeDocument(item.doc.id, true);
      } catch (_error) {
        updateItem(item.localId, {
          passwordVerifying: false,
          error: "This statement could not be removed.",
        });
        return;
      }
    }
    clearPasswordVerifiedTimer(item.localId);
    setItems((current) =>
      current.filter((candidate) => candidate.localId !== item.localId)
    );
    autoAttemptKeyRef.current = "";
  };

  const uploadedDocIds = useMemo(
    () =>
      items
        .filter((item) => item.status === "uploaded" && item.doc?.id)
        .map((item) => item.doc!.id),
    [items]
  );
  const allUploaded =
    !!items.length && items.every((item) => item.status === "uploaded");
  const attentionCount = items.filter((item) =>
    RETRYABLE_UPLOAD_STATUSES.has(item.status)
  ).length;
  const orderedItems = useMemo(
    () =>
      sortByPriority(
        items,
        (item) => {
          if (item.status === "password_required") return 0;
          if (
            IN_PROGRESS_UPLOAD_STATUSES.has(item.status) ||
            (analysisLoading && item.status === "uploaded")
          ) {
            return 1;
          }
          return 2;
        },
        (item) => item.addedAt || Number(item.localId.split("-")[0]) || 0
      ),
    [analysisLoading, items]
  );
  const isFirstTimeReview = firstTimeVariant === "review";
  const hasReviewListContent = !!items.length || !!reviewListChildren;
  const hasAccountListContent = !!items.length || !!accountListChildren;

  const submitReadyItems = async () => {
    if (!onReady || !allUploaded || !uploadedDocIds.length) return;
    setItems((current) =>
      current.map((item) =>
        item.rangeUpload ? { ...item, rangeProcessing: true } : item
      )
    );
    setAnalysisLoading(true);
    setAnalysisError("");
    try {
      await onReady(uploadedDocIds);
      setItems([]);
      autoAttemptKeyRef.current = "";
    } catch (error) {
      setItems((current) =>
        current.map((item) =>
          item.rangeUpload ? { ...item, rangeProcessing: false } : item
        )
      );
      setAnalysisError(
        getApiErrorMessage(error, "Unable to submit these statements for analysis.")
      );
    } finally {
      setAnalysisLoading(false);
    }
  };

  useEffect(() => {
    onItemsChange?.(items);
    setScopeBusy(
      scope,
      items.some((item) => item.status !== "uploaded") || analysisLoading
    );
  }, [items, analysisLoading, onItemsChange, scope, setScopeBusy]);

  useEffect(
    () => () => setScopeBusy(scope, false),
    [scope, setScopeBusy]
  );

  useEffect(() => {
    if (!autoSubmit || !allUploaded || !uploadedDocIds.length || !onReady) return;
    const key = uploadedDocIds.slice().sort((a, b) => a - b).join(",");
    if (autoAttemptKeyRef.current === key) return;
    autoAttemptKeyRef.current = key;
    submitReadyItems();
  }, [autoSubmit, allUploaded, uploadedDocIds, onReady]);

  return (
    <div
      className={`${styles.uploadQueueRoot} ${
        dialogVariant ? styles.dialogUploadQueue : ""
      } ${
        firstTimeVariant === "landing" ? styles.firstTimeLandingUploadQueue : ""
      } ${
        firstTimeVariant === "review" ? styles.firstTimeReviewUploadQueue : ""
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        className={styles.hiddenInput}
        onChange={(event) => {
          const rangeUpload = pendingRangeUploadRef.current;
          pendingRangeUploadRef.current = undefined;
          void addFiles(Array.from(event.target.files || []), rangeUpload);
        }}
      />
      {!hideTrigger ? (
        <UploadDropzone
          compact={compact}
          dialogVariant={dialogVariant}
          variant={
            firstTimeVariant === "landing"
              ? "firstTimeLanding"
              : firstTimeVariant === "review"
                ? "firstTimeReview"
                : "default"
          }
          onBrowse={() => {
            pendingRangeUploadRef.current = undefined;
            inputRef.current?.click();
          }}
          onFilesDropped={(files) => void addFiles(files)}
        />
      ) : null}
      {listHeader}

      {isFirstTimeReview && hasReviewListContent ? (
        <div className={styles.firstTimeReviewFilesViewport}>
          {items.length ? (
            <div className={styles.queueList}>
              {orderedItems.map((item) => (
                <UploadQueueItem
                  key={item.localId}
                  item={item}
                  dialogVariant={dialogVariant}
                  analysisLoading={analysisLoading}
                  onViewError={(message) => {
                    if (message) setAnalysisError(message);
                  }}
                  onRemove={() => removeItem(item)}
                  onPasswordChange={(password) =>
                    updateItem(item.localId, { password, error: undefined })
                  }
                  onPasswordSubmit={() => submitPassword(item)}
                />
              ))}
            </div>
          ) : null}
          {reviewListChildren}
        </div>
      ) : hasAccountListContent ? (
        <>
          {dialogVariant && items.length ? (
            <div className={styles.dialogQueueHeader}>
              <strong>Statements added ({items.length})</strong>
              {attentionCount ? (
                <span className={styles.attentionChip}>
                  <HiOutlineExclamationCircle />
                  {attentionCount} {attentionCount === 1 ? "file" : "files"}{" "}
                  needs attention
                </span>
              ) : null}
            </div>
          ) : null}
          <div
            className={`${styles.queueList} ${
              accountListChildren ? styles.accountStatementList : ""
            }`}
          >
            {orderedItems.map((item) => (
              <UploadQueueItem
                key={item.localId}
                item={item}
                dialogVariant={dialogVariant}
                analysisLoading={analysisLoading}
                onViewError={(message) => {
                  if (message) setAnalysisError(message);
                }}
                onRemove={() => removeItem(item)}
                onPasswordChange={(password) =>
                  updateItem(item.localId, { password, error: undefined })
                }
                onPasswordSubmit={() => submitPassword(item)}
              />
            ))}
            {accountListChildren}
          </div>
        </>
      ) : null}

      {analysisError ? (
        <div className={styles.analysisRetry}>
          <span>{analysisError}</span>
          <RetryAction loading={analysisLoading} onClick={submitReadyItems} />
        </div>
      ) : null}
    </div>
  );
});

FileUploadQueue.displayName = "FileUploadQueue";
